import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Trash2, X, Sparkles, AlertTriangle, Phone, CalendarClock, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTimeIT } from "@/lib/format";
import { toast } from "sonner";

// Polling interval
const POLL_MS = 30_000; // every 30s for new notifications
const SCAN_MS = 5 * 60_000; // every 5 minutes trigger a scan

const TYPE_ICON = {
  segnalazione: Flame,
  lead_cold: AlertTriangle,
  stale_practice: AlertTriangle,
  upcoming_appt: CalendarClock,
  recall_today: Phone,
  system: Sparkles,
};
const SEV_BG = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  info: "bg-sky-50 text-sky-700 border-sky-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  critical: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const dropdownRef = useRef(null);
  const lastSeenIdsRef = useRef(new Set());

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications", { params: { limit: 30 } });
      const newItems = r.data.items || [];
      setItems(newItems);
      setUnread(r.data.unread || 0);

      // Show desktop notification only for items NOT yet seen
      const newOnes = newItems.filter((n) => !n.read && !lastSeenIdsRef.current.has(n.id));
      newOnes.forEach((n) => showDesktopNotification(n));
      newItems.forEach((n) => lastSeenIdsRef.current.add(n.id));
    } catch (e) {
      console.warn("Notifications load failed:", e?.response?.status || e?.message);
    }
  }, []);

  const scan = useCallback(async () => {
    try {
      await api.post("/notifications/scan");
      await load();
    } catch (e) {
      console.warn("Notifications scan failed:", e?.response?.status || e?.message);
    }
  }, [load]);

  useEffect(() => {
    load();
    scan();
    const t1 = setInterval(load, POLL_MS);
    const t2 = setInterval(scan, SCAN_MS);

    // Request browser permission
    if ("Notification" in window && Notification.permission === "default") {
      // Will ask after first user interaction
    }
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [load, scan]);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const requestPermission = async () => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "default") {
      const p = await Notification.requestPermission();
      if (p === "granted") toast.success("Notifiche desktop attivate");
    }
  };

  const markAllRead = async () => {
    await api.post("/notifications/mark-read", { all: true });
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };
  const markRead = async (id) => {
    await api.post("/notifications/mark-read", { ids: [id] });
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
  };
  const remove = async (id, e) => {
    e?.stopPropagation();
    await api.delete(`/notifications/${id}`);
    setItems((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => { setOpen(!open); requestPermission(); }}
        data-testid="notif-bell"
        className="relative p-2 hover:bg-white/10 rounded-sm text-slate-300 hover:text-white transition"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-rose-500 text-white rounded-full flex items-center justify-center tabular-nums" data-testid="notif-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[420px] max-h-[600px] bg-white border border-slate-200 rounded-sm shadow-2xl z-50 flex flex-col overflow-hidden" data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#F0F2F5] bg-slate-50">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: "var(--gold)" }}>Notifiche</div>
              <h3 className="font-serif text-base text-slate-900">{unread > 0 ? `${unread} non lette` : "Tutto a posto"}</h3>
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAllRead} data-testid="notif-mark-all" className="text-xs text-slate-600 hover:text-slate-900 px-2 py-1 hover:bg-white rounded-sm flex items-center gap-1">
                  <Check className="w-3 h-3" /> Segna tutte
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-white rounded-sm text-slate-500"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="text-center text-slate-500 text-sm py-12 px-4">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                Nessuna notifica.<br />Quando arriva qualcosa di importante, qui te lo dico io.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((n) => {
                  const Icon = TYPE_ICON[n.type] || Sparkles;
                  return (
                    <li key={n.id} data-testid={`notif-${n.id}`} className={`relative ${n.read ? "" : "bg-amber-50/30"}`}>
                      <button
                        onClick={() => { if (!n.read) markRead(n.id); }}
                        className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-[#F6F8FB] transition-colors transition"
                      >
                        <div className={`w-9 h-9 rounded-sm border flex items-center justify-center shrink-0 ${SEV_BG[n.severity] || SEV_BG.info}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-sm font-semibold text-slate-900 leading-snug">{n.title}</div>
                            {!n.read && <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0 mt-1.5" />}
                          </div>
                          {n.message && <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">{n.message}</div>}
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-[10px] text-slate-400 tabular-nums">{fmtDateTimeIT(n.created_at)}</span>
                            {n.link && (
                              <Link to={n.link} onClick={() => setOpen(false)} className="text-[11px] text-[#8a6e3d] hover:underline font-medium">
                                Apri →
                              </Link>
                            )}
                          </div>
                        </div>
                      </button>
                      <button onClick={(e) => remove(n.id, e)} className="absolute top-2 right-2 p-1 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-sm opacity-0 group-hover:opacity-100">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="px-4 py-2 border-t border-[#F0F2F5] text-[11px] text-slate-500 text-center bg-slate-50">
            Auto-refresh ogni 30 sec • Scan alert ogni 5 min
          </div>
        </div>
      )}
    </div>
  );
}

function showDesktopNotification(n) {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    const notif = new Notification(n.title, {
      body: n.message || "",
      icon: "/icons/icon-192.png",
      tag: n.id,
      badge: "/icons/icon-192.png",
      requireInteraction: n.severity === "critical",
    });
    notif.onclick = () => {
      window.focus();
      if (n.link) window.location.href = n.link;
      notif.close();
    };
  } catch {}
}
